import { supabase } from "@/lib/supabase";
import type { Job, Issue, Credit, LeadSource } from "@/data/types";

// ── Disposition Types ────────────────────────────────────────────────────────────────

export type DispositionReason =
  | "Duplicate Lead"
  | "Out of Service Area"
  | "Unqualified Customer"
  | "Bad Contact Info"
  | "Price Sensitivity"
  | "Competitor Won"
  | "Other";

export const DISPOSITION_REASONS: DispositionReason[] = [
  "Duplicate Lead",
  "Out of Service Area",
  "Unqualified Customer",
  "Bad Contact Info",
  "Price Sensitivity",
  "Competitor Won",
  "Other",
];

export interface DispositionData {
  name: string;
  sourceType: string;
  totalLeads: number;
  contacted: number;
  noContact: number;
  appointmentsSet: number;
  appointmentsCompleted: number;
  noShow: number;
  sold: number;
  rejected: number;
  rejectionReasons: Record<DispositionReason, number>;
  revenue: number;
  cost: number;
  issueCount: number;
  creditAmount: number;
  conversionRate: number;
}

export interface DispositionTotals {
  totalLeads: number;
  contacted: number;
  noContact: number;
  appointmentsSet: number;
  appointmentsCompleted: number;
  noShow: number;
  sold: number;
  rejected: number;
  revenue: number;
  cost: number;
}

export interface ReasonTotals {
  reason: DispositionReason;
  count: number;
}

export interface DispositionFilters {
  sourceFilter: "all" | string;
  dateFrom: string;
  dateTo: string;
}

export interface EmailReportPayload {
  to: string;
  subject: string;
  notes: string;
  dateFrom: string;
  dateTo: string;
  data: DispositionData[];
  totals: DispositionTotals;
  reasonTotals: Record<DispositionReason, number>;
}

// ── Disposition Service ────────────────────────────────────────────────────────────────

export const DispositionService = {
  /**
   * Calculate disposition data for each lead source
   */
  calculateDispositionData(
    leadSources: LeadSource[],
    jobs: Job[],
    issues: Issue[],
    credits: Credit[],
    filters: DispositionFilters
  ): DispositionData[] {
    const { dateFrom, dateTo } = filters;
    const fromDate = new Date(dateFrom);
    const toDate = new Date(dateTo);
    toDate.setHours(23, 59, 59, 999);

    return leadSources.map((src) => {
      const linkedJobs = jobs.filter((j) => {
        if (j.marketing?.leadSource !== src.name) return false;
        const jobDate = j.scheduledInstallDate || j.targetInstallDate;
        if (!jobDate) return true;
        const d = new Date(jobDate);
        return d >= fromDate && d <= toDate;
      });

      const revenue = linkedJobs.reduce(
        (sum, j) => sum + (j.financials?.contractValue ?? j.payoutAmount),
        0
      );
      const sold = linkedJobs.filter(
        (j) => j.currentStage === "Completed" || j.marketing?.saleOutcome === "Closed"
      ).length;
      const noContact = src.total_leads - src.contacted;
      const noShow = src.appointments_set - src.appointments_completed;
      const totalRejected = Object.values(src.rejection_reasons ?? {}).reduce(
        (sum, v) => sum + v,
        0
      );
      const issueCount = issues.filter((i) =>
        linkedJobs.some((j) => j.id === i.jobId)
      ).length;
      const creditAmount = credits.filter((c) =>
        linkedJobs.some((j) => j.id === c.jobId)
      ).reduce((sum, c) => sum + c.creditAmount, 0);

      return {
        name: src.name,
        sourceType: src.source_type,
        totalLeads: src.total_leads,
        contacted: src.contacted,
        noContact,
        appointmentsSet: src.appointments_set,
        appointmentsCompleted: src.appointments_completed,
        noShow,
        sold,
        rejected: totalRejected,
        rejectionReasons: (src.rejection_reasons ?? {}) as Record<DispositionReason, number>,
        revenue,
        cost: src.monthly_cost || 0,
        issueCount,
        creditAmount,
        conversionRate:
          src.total_leads > 0
            ? Math.round((sold / src.total_leads) * 100)
            : 0,
      };
    });
  },

  /**
   * Filter disposition data by source type
   */
  filterBySourceType(
    data: DispositionData[],
    sourceFilter: "all" | string
  ): DispositionData[] {
    if (sourceFilter === "all") return data;
    return data.filter((d) => d.sourceType === sourceFilter);
  },

  /**
   * Calculate totals from filtered data
   */
  calculateTotals(data: DispositionData[]): DispositionTotals {
    return {
      totalLeads: data.reduce((s, d) => s + d.totalLeads, 0),
      contacted: data.reduce((s, d) => s + d.contacted, 0),
      noContact: data.reduce((s, d) => s + d.noContact, 0),
      appointmentsSet: data.reduce((s, d) => s + d.appointmentsSet, 0),
      appointmentsCompleted: data.reduce((s, d) => s + d.appointmentsCompleted, 0),
      noShow: data.reduce((s, d) => s + d.noShow, 0),
      sold: data.reduce((s, d) => s + d.sold, 0),
      rejected: data.reduce((s, d) => s + d.rejected, 0),
      revenue: data.reduce((s, d) => s + d.revenue, 0),
      cost: data.reduce((s, d) => s + d.cost, 0),
    };
  },

  /**
   * Calculate aggregated rejection reasons
   */
  calculateReasonTotals(data: DispositionData[]): Record<DispositionReason, number> {
    const map: Record<DispositionReason, number> = {} as any;
    DISPOSITION_REASONS.forEach((r) => {
      map[r] = 0;
    });
    data.forEach((d) => {
      DISPOSITION_REASONS.forEach((r) => {
        map[r] += d.rejectionReasons[r] || 0;
      });
    });
    return map;
  },

  /**
   * Get unique source types from data
   */
  getSourceTypes(data: DispositionData[]): string[] {
    return [...new Set(data.map((d) => d.sourceType))];
  },

  /**
   * Export disposition report to CSV
   */
  exportToCSV(
    data: DispositionData[],
    dateFrom: string,
    dateTo: string
  ): { headers: string[]; rows: (string | number)[][] } {
    const reasonHeaders = DISPOSITION_REASONS.map((r) => `Rej: ${r}`);

    const rows = data.map((d) => [
      d.name,
      d.sourceType,
      d.totalLeads,
      d.contacted,
      d.noContact,
      d.appointmentsSet,
      d.appointmentsCompleted,
      d.noShow,
      d.sold,
      d.rejected,
      d.revenue,
      d.cost,
      d.conversionRate,
      ...DISPOSITION_REASONS.map((r) => d.rejectionReasons[r] || 0),
    ]);

    const headers = [
      "Source",
      "Type",
      "Total Leads",
      "Contacted",
      "No Contact",
      "Appts Set",
      "Appts Completed",
      "No-Show",
      "Sold",
      "Rejected/Invalid",
      "Revenue",
      "Cost",
      "Conversion %",
      ...reasonHeaders,
    ];

    return { headers, rows };
  },

  /**
   * Send disposition report via email
   */
  async sendEmailReport(payload: EmailReportPayload): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-disposition-report",
        {
          body: payload,
        }
      );

      if (error) throw error;
      return { success: true };
    } catch (err) {
      console.error("Failed to send disposition report email:", err);
      return {
        success: false,
        error: err instanceof Error ? err.message : "Unknown error",
      };
    }
  },

  /**
   * Generate HTML content for email
   */
  generateEmailHTML(payload: EmailReportPayload): string {
    const { totals, reasonTotals, dateFrom, dateTo } = payload;

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Lead Disposition Report</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 700px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #1e3a5f, #2d5a87); padding: 25px; border-radius: 12px 12px 0 0; text-align: center;">
      <h1 style="color: white; margin: 0; font-size: 22px;">Lead Disposition Report</h1>
      <p style="color: rgba(255,255,255,0.8); margin: 8px 0 0 0; font-size: 14px;">${dateFrom} to ${dateTo}</p>
    </div>
    
    <div style="background: #f9f9f9; padding: 25px; border-radius: 0 0 12px 12px;">
      <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 20px;">
        <div style="background: white; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e0e0e0;">
          <p style="font-size: 24px; font-weight: bold; margin: 0; color: #1e3a5f;">${totals.totalLeads}</p>
          <p style="font-size: 11px; color: #666; margin: 4px 0 0 0;">Total Leads</p>
        </div>
        <div style="background: white; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e0e0e0;">
          <p style="font-size: 24px; font-weight: bold; margin: 0; color: #22c55e;">${totals.contacted}</p>
          <p style="font-size: 11px; color: #666; margin: 4px 0 0 0;">Contacted</p>
        </div>
        <div style="background: white; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e0e0e0;">
          <p style="font-size: 24px; font-weight: bold; margin: 0; color: #22c55e;">${totals.sold}</p>
          <p style="font-size: 11px; color: #666; margin: 4px 0 0 0;">Sold</p>
        </div>
        <div style="background: white; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e0e0e0;">
          <p style="font-size: 24px; font-weight: bold; margin: 0; color: #ef4444;">${totals.rejected}</p>
          <p style="font-size: 11px; color: #666; margin: 4px 0 0 0;">Rejected</p>
        </div>
        <div style="background: white; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e0e0e0;">
          <p style="font-size: 24px; font-weight: bold; margin: 0; color: #1e3a5f;">$${totals.revenue.toLocaleString()}</p>
          <p style="font-size: 11px; color: #666; margin: 4px 0 0 0;">Revenue</p>
        </div>
      </div>
    </div>
  </body>
</html>`;
  },
};